/**
 * The ONE place platen copy is built (Phase 86): the size-range formatter,
 * the platen variant name and the "{platen} {variant}" group name. Every
 * surface (Settings, product sync, Product updates, the missing-files dialog,
 * print issues) calls these, so the words and their zh translations cannot
 * drift between surfaces.
 *
 * The size-range formatter (UI-SPEC S1). Every surface that
 * prints which sizes a platen variant covers ("14x16 Small: XS to L", the
 * worklist, the missing-files dialog) calls this, so the wording cannot drift.
 *
 * Rule:
 *   - exactly one size prints that size alone (never "XL to XL");
 *   - sizes forming ONE contiguous run of two or more print
 *     `common:platenSizeRange.run` ("XS to L", zh "XS至L");
 *   - anything else prints every size in rank order joined by
 *     `common:platenSizeRange.separator` ("XS, M, L", zh "XS、M、L").
 *
 * Ordering and contiguity come from the shared `buildSizeRuns`, which defaults
 * to the shared size ladder when no `orderedUniverse` is given; this helper
 * never sorts on its own. Keys are namespaced (`common:`) so a `t` bound to
 * any namespace works.
 */
import type { TFunction } from 'i18next';
import { buildSizeRuns } from '@threadheads/shared';

export function formatPlatenSizeRange(
  t: TFunction,
  sizes: readonly string[],
  orderedUniverse?: readonly string[],
): string {
  const runs = buildSizeRuns(sizes, orderedUniverse);
  const ordered = runs.flat();
  if (ordered.length === 0) return '';
  if (ordered.length === 1) return ordered[0]!;
  if (runs.length === 1) {
    const run = runs[0]!;
    return t('common:platenSizeRange.run', { from: run[0], to: run[run.length - 1] });
  }
  return ordered.join(t('common:platenSizeRange.separator'));
}

/**
 * The operator-facing name of a platen variant (`Small` -> "Small" / "小号").
 * A code with no translation (a variant added to the shared list before its
 * copy) prints the code itself. Select and filter VALUES stay the code.
 */
export function platenVariantLabel(t: TFunction, variant: string): string {
  // `ns` rather than a `common:` prefix, so the i18n check sees the key family.
  return t(`platenVariantName.${variant}`, { ns: 'common', defaultValue: variant });
}

/** "{platen} {variant}" for one platen variant, for example "14x16 Small". */
export function platenGroupLabel(t: TFunction, assignment: { platen: string; platenVariant: string }): string {
  return t('common:platenGroupName', {
    platen: assignment.platen,
    variant: platenVariantLabel(t, assignment.platenVariant),
  });
}
